if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        if (this.dataset.desktopLayout === 'thumbnail_slider') {
          // The thumbnail strip's own prev/next buttons should page through the main
          // gallery (one media at a time) instead of just scrolling the thumbnail strip.
          // Intercepting in the capture phase stops slider-component's own click handler
          // (bound directly on the buttons) from also firing its group scroll-by-3.
          this.elements.thumbnails.addEventListener('click', this.onThumbnailNavClick.bind(this), true);
          // slider-component disables these buttons whenever the thumbnail strip itself
          // has nothing left to scroll (e.g. only 3 thumbnails, all fitting on screen) —
          // but they now navigate the main gallery, so that's irrelevant, and it would
          // otherwise keep re-disabling them (on scroll/resize) after we set them below.
          // enableSliderLooping short-circuits that logic in slider-component's update().
          this.elements.thumbnails.enableSliderLooping = true;
          this.updateThumbnailNavButtons();

          // Mobile already pages by touch natively (overflow-x + scroll-snap). Add
          // mouse-drag support too, so the same slider can be dragged with a cursor.
          this.setupViewerDrag();
        }
      }

      setupViewerDrag() {
        const list = this.elements.viewer.slider;
        if (!list) return;

        this.viewerDrag = { isDown: false, dragged: false, startX: 0, scrollLeftStart: 0 };

        list.addEventListener('pointerdown', this.onViewerPointerDown.bind(this));
        window.addEventListener('pointermove', this.onViewerPointerMove.bind(this));
        window.addEventListener('pointerup', this.onViewerPointerUp.bind(this));
        // Capture phase so a drag-release doesn't also open the image zoom/lightbox,
        // which otherwise covers the whole slide as a click target.
        list.addEventListener('click', this.onViewerClickCapture.bind(this), true);
      }

      onViewerPointerDown(event) {
        if (event.pointerType !== 'mouse') return;
        // Otherwise the browser's native image-drag ghost hijacks pointermove.
        event.preventDefault();
        const drag = this.viewerDrag;
        drag.isDown = true;
        drag.dragged = false;
        drag.startX = event.clientX;
        drag.scrollLeftStart = this.elements.viewer.slider.scrollLeft;
        this.elements.viewer.slider.classList.add('is-dragging');
      }

      onViewerPointerMove(event) {
        const drag = this.viewerDrag;
        if (!drag || !drag.isDown) return;
        const delta = event.clientX - drag.startX;
        if (Math.abs(delta) > 5) drag.dragged = true;
        this.elements.viewer.slider.scrollLeft = drag.scrollLeftStart - delta;
      }

      onViewerPointerUp(event) {
        const drag = this.viewerDrag;
        if (!drag || !drag.isDown) return;
        drag.isDown = false;
        this.elements.viewer.slider.classList.remove('is-dragging');
        if (drag.dragged) this.settleViewerDrag(event.clientX - drag.startX);
        // Allow the click suppression to run before it's reset.
        requestAnimationFrame(() => {
          drag.dragged = false;
        });
      }

      onViewerClickCapture(event) {
        if (this.viewerDrag && this.viewerDrag.dragged) {
          event.preventDefault();
          event.stopPropagation();
        }
      }

      // Always land on exactly the next/previous slide (one at a time), never further —
      // dragging a long way or fast shouldn't skip past several slides in one go.
      settleViewerDrag(delta) {
        const viewer = this.elements.viewer;
        const items = viewer.sliderItemsToShow;
        const offset = viewer.sliderItemOffset;
        if (!items || !items.length || !offset) return;

        const startIndex = Math.round(this.viewerDrag.scrollLeftStart / offset);
        const threshold = offset * 0.2;
        let targetIndex = startIndex;
        if (delta <= -threshold) targetIndex += 1;
        else if (delta >= threshold) targetIndex -= 1;
        targetIndex = Math.max(0, Math.min(items.length - 1, targetIndex));

        viewer.slider.scrollTo({ left: items[targetIndex].offsetLeft, behavior: 'smooth' });
      }

      updateThumbnailNavButtons() {
        const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        const activeIndex = thumbnails.findIndex((item) => item.querySelector('button').hasAttribute('aria-current'));
        const prevButton = this.elements.thumbnails.querySelector('button[name="previous"]');
        const nextButton = this.elements.thumbnails.querySelector('button[name="next"]');
        if (prevButton) prevButton.disabled = activeIndex <= 0;
        if (nextButton) nextButton.disabled = activeIndex === -1 || activeIndex >= thumbnails.length - 1;
      }

      onThumbnailNavClick(event) {
        const button = event.target.closest('button[name="previous"], button[name="next"]');
        if (!button) return;
        event.preventDefault();
        event.stopPropagation();

        // Navigate relative to the thumbnail strip (not the main media list), since
        // hide_variants filtering can make the main list shorter than the thumbnails.
        const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        const activeIndex = thumbnails.findIndex((item) => item.querySelector('button').hasAttribute('aria-current'));
        if (activeIndex === -1) return;

        const nextIndex = button.name === 'next' ? activeIndex + 1 : activeIndex - 1;
        if (nextIndex < 0 || nextIndex >= thumbnails.length) return;

        this.setActiveMedia(thumbnails[nextIndex].dataset.target, false);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.dataset.desktopLayout === 'thumbnail_slider') this.updateThumbnailNavButtons();

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
